import * as React from "react";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  Button,
  useTheme,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { useNotification } from "../../provider/NotificationProvider";
import { useUser } from "../../provider/AuthProvider";
import { useNavigate } from "react-router-dom";
import stringToColor from "../../services/stringToColor";
import {
  AccountCircle,
  Cancel,
  Edit,
  EmailRounded,
  InsertPhoto,
  LogoutSharp,
  Phone,
  Translate,
} from "@mui/icons-material";
import CompoundLogoDialog from "../../components/CompoundLogoDialog";
import LangSwitcher from "../../components/languageswitcher";
import { useTranslation } from "react-i18next";

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const { unreadCount, notifications, markOneAsRead, fetchNotifications } =
    useNotification();

  const [compoundLogo, setCompoundLogo] = useState(user?.compoundLogo || "");
  const [compoundName, setCompoundName] = useState(user?.compoundName);
  const [compoundArabicName, setCompoundArabicName] = useState(
    user?.compoundArabicName
  );
  const [logoLoading, setLogoLoading] = useState(!!user?.compoundLogo);
  const { t, i18n } = useTranslation();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";
  useEffect(() => {
    if (user?.compoundLogo) {
      setCompoundLogo(user.compoundLogo);
      setLogoLoading(true);
    }
  }, [user?.compoundLogo]);

  const [anchorNotif, setAnchorNotif] = useState(null);
  const openNotif = Boolean(anchorNotif);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);

  const [anchorProfile, setAnchorProfile] = useState(null);
  const openProfile = Boolean(anchorProfile);

  const handleProfileOpen = (e) => setAnchorProfile(e.currentTarget);
  const handleProfileClose = () => setAnchorProfile(null);

  const handleNotifOpen = async (e) => {
    setAnchorNotif(e.currentTarget);
    await fetchNotifications();
  };
  const handleNotifClose = () => setAnchorNotif(null);

  const handleProfile = () => {
    handleProfileClose();
    navigate("/profile");
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      ml={2}
      p="2px 8px"
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#0d1b2a" : "#f5f7fa",
        transition: "background-color 0.3s",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#22374D" : "#E3E3E3",
        },
        borderRadius: "12px",
        backdropFilter: "blur(20px)",
      }}
    >
      <Box px={2} py={1} display={"flex"}>
        <img
          alt="main-logo"
          src={`../../assets/icons/logo_text.png`}
          style={{
            cursor: "pointer",
            width: "40%",
            height: "auto",
            maxHeight: "10vh",
            minWidth: "100px",
          }}
        />
        <Box display={"flex"} alignItems={"center"} gap={2} ml={20}>
          {compoundLogo ? (
            <Avatar
              className="notranslate"
              alt="Compound"
              src={compoundLogo}
              onLoad={() => setLogoLoading(false)}
              onError={() => setLogoLoading(false)}
            />
          ) : (
            <Avatar
              sx={{ bgcolor: stringToColor(compoundLogo) }}
              className="notranslate"
            >
              {compoundLogo?.charAt(0)?.toUpperCase()}
            </Avatar>
          )}
          <Typography variant="h4" fontStyle={"italic"} className="notranslate">
            {i18n.language === "ar" && user?.compoundArabicName?.trim()
              ? user.compoundArabicName
              : user?.compoundName}
          </Typography>
        </Box>
      </Box>

      <Box display="flex">
        <Tooltip title="Language">
          <LangSwitcher />
        </Tooltip>
        <Tooltip title={t("topbar.toggle_light_dark")}>
          <IconButton onClick={colorMode.toggleColorMode}>
            {theme.palette.mode === "dark" ? (
              <DarkModeOutlinedIcon fontSize="large" />
            ) : (
              <LightModeOutlinedIcon fontSize="large" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={t("topbar.notifications")}>
          <IconButton onClick={handleNotifOpen}>
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.7rem",
                },
              }}
            >
              <NotificationsOutlinedIcon fontSize="large" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorNotif}
          open={openNotif}
          onClose={handleNotifClose}
          PaperProps={{
            sx: {
              minWidth: 330,
              borderRadius: 2,
              mt: 1.5,
              boxShadow: 6,
              maxHeight: 400,
            },
          }}
        >
          <Box px={2} py={1}>
            <Typography fontWeight="bold">
              {t("topbar.notifications")}
            </Typography>
          </Box>
          <Divider />
          {notifications.length === 0 ? (
            <MenuItem disabled>{t("topbar.no_new_notifications")}</MenuItem>
          ) : (
            notifications.map((n) => (
              <MenuItem
                key={n.id}
                onClick={handleNotifClose}
                x={{ alignItems: "flex-start", py: 1.5 }}
              >
                <ListItem
                  disableGutters
                  disablePadding
                  sx={{ alignItems: "flex-start", display: "flex" }}
                >
                  <ListItemIcon>
                    <Avatar
                      className="notranslate"
                      src={n.sender?.avatar_url}
                      sx={{ bgcolor: stringToColor(n.sender?.FullName) }}
                    >
                      {n.sender?.FullName?.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="h5" ml={1}>
                          {n.title}
                        </Typography>
                        <Typography
                          variant="h6"
                          color="text.primary"
                          ml={1}
                          className="notranslate"
                        >
                          ({n.sender?.username || t("topbar.system_alert")})
                        </Typography>
                      </Box>
                    }
                    onClick={() => navigate("/notification")}
                  />
                </ListItem>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography ml={6}>
                    {new Date(n.createdAt).toLocaleDateString()}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      markOneAsRead(n.id);
                    }}
                  >
                    <Cancel />
                  </IconButton>
                </Box>
              </MenuItem>
            ))
          )}
        </Menu>

        <Tooltip title={t("topbar.account")}>
          <IconButton onClick={handleProfileOpen}>
            {user?.avatar_url ? (
              <Avatar
                className="notranslate"
                alt="Compound"
                src={user?.avatar_url}
                onLoad={() => setLogoLoading(false)}
                onError={() => setLogoLoading(false)}
              />
            ) : (
              <Avatar
                sx={{ bgcolor: stringToColor(user?.avatar_url) }}
                className="notranslate"
              >
                {user?.FullName?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorProfile}
          open={openProfile}
          onClose={handleProfileClose}
          PaperProps={{
            sx: {
              width: 300,
              borderRadius: 2,
              boxShadow: 4,
              mt: 1.5,
              overflow: "hidden",
            },
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
            position="relative"
            mb={5}
          >
            {logoLoading ? (
              <Skeleton variant="rounded" width={100} height={100} />
            ) : (
              <Avatar
                className="notranslate"
                src={compoundLogo || undefined}
                alt="Compound Logo"
                variant="rounded"
                sx={{ width: 100, height: 100, bgcolor: "#ccc" }}
                onLoad={() => setLogoLoading(false)}
                onError={() => setLogoLoading(false)}
              >
                {!compoundLogo && (
                  <InsertPhoto fontSize="large" sx={{ opacity: 0.4 }} />
                )}
              </Avatar>
            )}

            <IconButton
              onClick={() => setLogoDialogOpen(true)}
              sx={{
                position: "absolute",
                bottom: -10,
                backgroundColor: "primary.main",
                border: "1px solid #fff",
                [direction === "rtl" ? "left" : "right"]: "calc(50% - 50px)",
              }}
            >
              <Edit color="info" />
            </IconButton>
          </Box>

          <Divider mb={4} />

          <Box px={2} py={1.5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                className="notranslate"
                alt={user?.username}
                src={user?.avatar_url}
                sx={{
                  width: 56,
                  height: 56,
                  boxShadow: 2,
                  bgcolor: stringToColor(user?.FullName),
                }}
              >
                {!user?.avatar_url && user?.FullName?.charAt(0).toUpperCase()}
              </Avatar>
              <Box className="notranslate">
                <Typography fontWeight="bold" fontSize="1rem">
                  {user?.username}
                </Typography>
                <Box display="flex">
                  <EmailRounded sx={{ mr: 2 }} color="info" />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {user?.email}
                  </Typography>
                </Box>
                <Box display="flex">
                  <Phone sx={{ mr: 2 }} color="success" />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    dir="ltr"
                  >
                    {user?.phone}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          <Divider />

          <Box display="flex" justifyContent="center" gap={3}>
            <Button onClick={handleProfile} startIcon={<AccountCircle />}>
              {t("topbar.profile")}
            </Button>
            <Button onClick={logout} startIcon={<LogoutSharp />}>
              {t("topbar.logout")}
            </Button>
          </Box>
        </Menu>
      </Box>

      <CompoundLogoDialog
        open={logoDialogOpen}
        onClose={() => setLogoDialogOpen(false)}
        initialLogo={compoundLogo}
        userId={user?.id}
        onUpdated={(newUrl) => setCompoundLogo(newUrl)}
      />
    </Box>
  );
};

export default Topbar;
